// The button grammar (#11426), from the mock's `.btn` / `.seg`.
//
// `Btn` is the one shape for a control that performs or toggles something:
// surface ground, strong hairline, barely-there lift. `primary` renders
// ink-on-ink — the likely action, at most one per surface. `active` marks a
// toggled state by taking the ink border: state is shown by the border, not
// by a colour wash, because colour means judgement or category everywhere
// else in the system.
//
// `Seg` is the projection/mode switch: one bordered group of cells, the on
// cell in ink with the surface glyph. Each cell carries a MUI Tooltip — the
// cell is a glyph, so its name must be reachable — never a native `title`.

import React from "react";
import { Tooltip } from "@mui/material";

/**
 * The design system's disabled look (a withheld or disabled control): faded, with the
 * not-allowed cursor. `Btn` draws it, and any other control that must read as disabled in the
 * same family (a `Doorway`, a views-track segment) spreads it.
 */
export const WITHHELD_LOOK = {
  opacity: 0.55,
  cursor: "not-allowed",
} as const satisfies React.CSSProperties;

// Extends the native button contract so a wrapping MUI Tooltip works: the
// Tooltip clones its listeners (onMouseOver, onFocus, aria-describedby)
// onto its child, and a child that swallows them renders its popper never
// — the exact defect that shipped the Lock/Share tooltips dead (#11426,
// round-6 review). Everything unrecognised spreads through to the <button>.
export interface BtnProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  primary?: boolean;
  active?: boolean;
  /**
   * Icon-only variant (mock `.btn.fbtn.icon`): centered glyph at fixed
   * width, same ground/border/lift as the labelled shape so a row of tools
   * reads as one family. Height comes from padding + glyph, so it matches
   * its labelled neighbours rather than being a strict square. Always pair
   * with an aria-label and a Tooltip — a glyph must say.
   */
  icon?: boolean;
  /**
   * The LIVE state (mock `.btn.fbtn.live`): this control has changed what a
   * click on the surface DOES (the edit unlock). Speaks the eval tone —
   * distinct from `active`, which is the engaged/open-menu ink border.
   */
  live?: boolean;
  /**
   * The quiet utility (#11426 board-toolbar walkthrough, ruling 6): a
   * toolbar's secondary utilities rest as borderless ink-3 glyphs (ink-2
   * labels, for AA on the ground) on the page ground — no box, no lift — and take the bordered surface only on
   * hover. `active` and `live` override quiet: a control with something to
   * say takes the surface back.
   */
  quiet?: boolean;
}

export const Btn = React.forwardRef<HTMLButtonElement, BtnProps>(function Btn(
  {
    children,
    primary,
    active,
    disabled,
    icon,
    live,
    quiet,
    onMouseEnter,
    onMouseLeave,
    onClick,
    style,
    ...rest
  },
  ref
) {
  const [hover, setHover] = React.useState(false);
  // A withheld control (#11426 read-only ruling): `aria-disabled` renders
  // exactly like `disabled` — faded, not-allowed cursor, no hover lift, and
  // its click is swallowed here — but stays in the tab order, so the
  // tooltip explaining WHY it is withheld is reachable by keyboard. A native
  // `disabled` is unfocusable and its reason never reaches a keyboard user.
  const withheld =
    !!disabled ||
    rest["aria-disabled"] === true ||
    rest["aria-disabled"] === "true";
  // Whether a quiet button is actually resting: any engaged state (hover,
  // active toggle, live) takes the bordered surface back. A disabled quiet
  // button with a label keeps its quiet shape, borderless on the ground, and
  // only fades: its words say it is a control (#13370 round 41, a disabled
  // "Open in Documents" reads as the page's borderless utility). A disabled
  // quiet icon keeps the faded bordered box: a lone faded glyph has nothing
  // saying it is a control, and a disabled button fires no hover to surface
  // itself, so the box is what makes it, and its tooltip, discoverable.
  const resting = !!quiet && !hover && !active && !live && !(withheld && icon);
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      disabled={disabled}
      data-quiet={quiet ? "true" : undefined}
      aria-pressed={active === undefined ? undefined : active}
      onClick={withheld ? undefined : onClick}
      onMouseEnter={(e) => {
        setHover(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHover(false);
        onMouseLeave?.(e);
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--fp-gap-hair)",
        fontFamily: "var(--fp-font-ui)",
        fontSize: "var(--fp-t-meta)",
        fontWeight: "var(--fp-w-strong)" as unknown as number,
        letterSpacing: "var(--fp-track-body)",
        border: `1px solid ${
          resting
            ? "transparent"
            : live
              ? "var(--fp-st-eval)"
              : primary || active || (hover && !quiet)
                ? "var(--fp-ink)"
                : "var(--fp-border-strong)"
        }`,
        borderRadius: "7px",
        padding: icon ? "5px" : "5px 11px",
        width: icon ? "29px" : undefined,
        justifyContent: icon ? "center" : undefined,
        background: resting
          ? "transparent"
          : live && hover && !withheld
            ? "var(--fp-st-eval-bg)"
            : primary
              ? "var(--fp-ink)"
              : "var(--fp-surface)",
        // A resting quiet button sits on the page ground, where ink-3 is 4.4:1 (#11849 Round B,
        // accessibility S6): a label takes ink-2 to read at AA; a glyph keeps ink-3, which
        // clears the 3:1 a non-text control needs.
        color: resting
          ? icon
            ? "var(--fp-ink-3)"
            : "var(--fp-ink-2)"
          : live
            ? "var(--fp-st-eval)"
            : primary
              ? "var(--fp-surface)"
              : "var(--fp-ink)",
        cursor: withheld ? WITHHELD_LOOK.cursor : "pointer",
        opacity: withheld ? WITHHELD_LOOK.opacity : 1,
        // An ink-on-ink button cannot show state through its border, so its
        // hover feedback is the mock's brightness lift; a live button's is
        // its own tint above (its border already speaks the eval tone).
        filter: primary && hover && !withheld ? "brightness(1.35)" : undefined,
        boxShadow: resting ? "none" : "var(--fp-lift)",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </button>
  );
});

export interface SegOption<V extends string> {
  value: V;
  /** Accessible name — a mode switch's cell is a glyph, so it must say. */
  label: string;
  content: React.ReactNode;
}

export interface SegProps<V extends string> {
  options: ReadonlyArray<SegOption<V>>;
  value: V;
  onChange: (value: V) => void;
}

export const Seg = <V extends string>({
  options,
  value,
  onChange,
}: SegProps<V>): React.ReactElement => (
  <div
    role="group"
    style={{
      display: "inline-flex",
      border: "1px solid var(--fp-border-strong)",
      borderRadius: "7px",
      overflow: "hidden",
      background: "var(--fp-surface)",
      boxShadow: "var(--fp-lift)",
    }}
  >
    {options.map((opt, i) => {
      const on = opt.value === value;
      return (
        <Tooltip key={opt.value} title={opt.label}>
          <button
            type="button"
            aria-label={opt.label}
            aria-pressed={on}
            onClick={() => onChange(opt.value)}
            style={{
              border: "none",
              borderRight:
                i < options.length - 1 ? "1px solid var(--fp-border)" : "none",
              background: on ? "var(--fp-ink)" : "transparent",
              color: on ? "var(--fp-surface)" : "var(--fp-ink-3)",
              cursor: "pointer",
              padding: "5px 9px",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--fp-font-ui)",
              fontSize: "var(--fp-t-meta)",
            }}
          >
            {opt.content}
          </button>
        </Tooltip>
      );
    })}
  </div>
);
